import { getApps, initializeApp, type FirebaseApp } from 'firebase/app'
import { getMessaging, getToken, isSupported, onMessage, type Messaging, type MessagePayload } from 'firebase/messaging'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY

function isFirebaseConfigured(): boolean {
  return Boolean(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.messagingSenderId && firebaseConfig.appId)
}

let firebaseApp: FirebaseApp | null = null

function getFirebaseApp(): FirebaseApp | null {
  if (!isFirebaseConfigured()) {
    return null
  }
  if (!firebaseApp) {
    firebaseApp = getApps()[0] ?? initializeApp(firebaseConfig)
  }
  return firebaseApp
}

let messagingPromise: Promise<Messaging | null> | null = null

/** Web Push (and therefore FCM token retrieval) requires HTTPS/localhost and browser support. */
export function getFirebaseMessaging(): Promise<Messaging | null> {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      if (typeof window === 'undefined') {
        return null
      }

      const supported = await isSupported().catch(() => false)
      if (!supported) {
        return null
      }

      const app = getFirebaseApp()
      if (!app) {
        return null
      }

      return getMessaging(app)
    })()
  }

  return messagingPromise
}

/**
 * Registers the service worker Web Push needs for a token, requests notification
 * permission if not yet decided, and returns the FCM registration token.
 * Returns null when unsupported, unconfigured, or the user denies permission.
 */
export async function requestFcmToken(): Promise<string | null> {
  const messaging = await getFirebaseMessaging()
  if (!messaging || !VAPID_KEY) {
    return null
  }

  if (!('Notification' in window)) {
    return null
  }

  if (Notification.permission === 'default') {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return null
    }
  } else if (Notification.permission !== 'granted') {
    return null
  }

  try {
    // firebase-messaging-sw.js is a static /public file (not processed by Vite), so the
    // runtime config is passed via query string and read from self.location.search in the worker.
    const swConfigParams = Object.fromEntries(
      Object.entries(firebaseConfig).filter((entry): entry is [string, string] => Boolean(entry[1])),
    )
    const swUrl = `/firebase-messaging-sw.js?${new URLSearchParams(swConfigParams).toString()}`
    const registration = await navigator.serviceWorker.register(swUrl)
    return await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration })
  } catch (error) {
    console.error('[fcm] Failed to obtain a device token', error)
    return null
  }
}

/** Subscribes to messages that arrive while the app tab is open and focused. Returns an unsubscribe function. */
export function onForegroundMessage(callback: (payload: MessagePayload) => void): () => void {
  let unsubscribe: (() => void) | null = null
  let cancelled = false

  void getFirebaseMessaging().then((messaging) => {
    if (!messaging || cancelled) {
      return
    }
    unsubscribe = onMessage(messaging, callback)
  })

  return () => {
    cancelled = true
    unsubscribe?.()
  }
}
